import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import allUserGroups from 'enum/userGroups';
import UserStore from 'stores/UserStore';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import AchievementsModal from 'pages/Achievements/components/AchievementsModal';
import AchievementNotification from 'pages/Achievements/Notification/AchievementNotification';
import { getAchievements, dismissNotification } from 'pages/Achievements/Api';
import { DEFAULT_VALUE_ACHIEVEMENTS_POLLING_INTERVAL_MILLISECONDS } from 'enum/achievementsSettings';


import styles from './NotificationService.css';


const NotificationService = () => {
  const [showModal, setShowModal] = useState(false);
  const [achievementIndex, setAchievementIndex] = useState(0);
  const TIME_OUT = ConfigurationService.get(ConfigurationKeys.ACHIEVEMENTS_POLLING_INTERVAL_MILLISECONDS) ||
    DEFAULT_VALUE_ACHIEVEMENTS_POLLING_INTERVAL_MILLISECONDS;

  const userId = useSelector((store) => store.user.getIn(['user', 'userId']));
  const achievements = useSelector((store) => store.itemsAchievements.achievementsUnlocked);
  const dismissed = useSelector((store) => store.itemsAchievements.notifications.dismissed);
  const dispatch = useDispatch();

  const notifications = useMemo(() => {
    const visibleAchievements = achievements.filter(({ id }) => !dismissed?.includes(id));

    return visibleAchievements;
  }, [achievements, dismissed]);

  const onDismiss = useCallback((id) => {
    dispatch(dismissNotification(id));
  }, [dispatch]);

  const handleNextAchievement = useCallback(() => {
    const nextIndex = achievementIndex + 1;

    if (nextIndex < achievements.length) {
      setAchievementIndex(nextIndex);
    }
  }, [achievementIndex, achievements]);

  const handlePreviousAchievement = useCallback(() => {
    const prevIndex = achievementIndex - 1;

    if (prevIndex >= 0) {
      setAchievementIndex(prevIndex);
    }
  }, [achievementIndex]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setAchievementIndex(0);
  }, []);

  const openModal = useCallback((index) => {
    setAchievementIndex(index);
    setShowModal(true);
  }, []);

  useEffect(() => {
    if (!userId) {
      return () => undefined;
    }

    dispatch(getAchievements(userId));

    const interval = setInterval(() => {
      dispatch(getAchievements(userId));
    }, TIME_OUT);

    return () => clearInterval(interval);
  }, [userId, TIME_OUT, dispatch]);

  const renderNotification = useCallback((notification) => {
    const { id, name } = notification;

    return (
      <AchievementNotification
        key={id}
        id={id}
        name={name}
        onClick={openModal}
        onDismiss={onDismiss}
      />
    );
  }, [onDismiss, openModal]);

  if (!UserStore.hasCF(allUserGroups.ACHIEVEMENTS_SECTION)) {
    return null;
  }

  return (
    <Fragment>
      <AchievementsModal
        achievement={achievements[achievementIndex]}
        open={showModal}
        performerName=""
        onClose={closeModal}
        onNextPage={handleNextAchievement}
        onPreviousPage={handlePreviousAchievement}
      />

      <div className={styles.notificationsContainer}>
        {notifications?.map(renderNotification)}
      </div>
    </Fragment>
  );
};

export default NotificationService;
